import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  FilterFieldDefinition,
  FilterFieldKey,
  FilterOperator,
  FilterValue,
  TaskFilter,
} from '@/lib/types/filter'
import type { DuePreset } from '@/lib/filters/duePresets'
import { FILTER_FIELD_DEFINITIONS, OPERATOR_LABELS } from '@/lib/filters'
import { OPERATOR_MENU_LABELS } from '@/lib/filters/operatorLabels'
import { isDueDateOperator } from '@/lib/filters/fieldDefinitions'
import { DUE_PRESET_LABELS } from '@/lib/filters/duePresets'
import { useCycles, useMembers, useProjects } from '@/lib/db/rxdb/hooks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useAllLabels } from '@/lib/hooks/useLabels'
import useLoginStore from '@/store/LoginStore'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'

function getFieldDef(key: FilterFieldKey): FilterFieldDefinition | undefined {
  return (FILTER_FIELD_DEFINITIONS as Partial<Record<FilterFieldKey, FilterFieldDefinition>>)[key]
}

/**
 * Words for filters, so chips, the typed-filter preview and the zero-results
 * hint all read the same: "Status is In progress or Planned" (design 9a).
 */
export function useFilterLabels() {
  const { t } = useTranslation('tasks')
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const {
    all: { statuses },
  } = useStatuses()
  const { members } = useMembers()
  const { labels } = useAllLabels(workspaceId)
  const { projects } = useProjects({ isDeleted: false })
  const { cycles } = useCycles()
  const fmt = useDateTimeFormatters()

  const lookupMaps = useMemo(
    () => ({
      status: new Map(statuses.map((s) => [s.id, s.name])),
      member: new Map(members.map((m) => [m.userId, m.name ?? m.email ?? 'Unknown'])),
      label: new Map(labels.map((l) => [l.id, l.name])),
      project: new Map(
        projects.filter((p) => !p.isDeleted).map((p) => [p.id, p.name || t('filterBar.untitledProject')]),
      ),
      cycle: new Map(cycles.filter((c) => !c.isDeleted).map((c) => [c.id, c.name || 'Untitled cycle'])),
    }),
    [statuses, members, labels, projects, cycles, t],
  )

  const fieldLabel = useCallback(
    (key: FilterFieldKey, variant: 'chip' | 'menu' | 'plural' = 'chip'): string => {
      const def = getFieldDef(key)
      if (!def) return key
      // Custom fields carry the user's own name for them.
      if (key.startsWith('cf_')) return variant === 'plural' ? def.pluralLabel : def.label
      if (variant === 'plural') return t(`filters.fieldsPlural.${key}`, { defaultValue: def.pluralLabel })
      if (variant === 'menu' && def.menuLabel) {
        return t(`filters.fieldsMenu.${key}`, { defaultValue: def.menuLabel })
      }
      return t(`filters.fields.${key}`, { defaultValue: def.label })
    },
    [t],
  )

  const operatorLabel = useCallback(
    (op: FilterOperator, variant: 'chip' | 'menu' = 'chip'): string => {
      if (variant === 'menu' && OPERATOR_MENU_LABELS[op]) {
        return t(`filters.operatorsMenu.${op}`, { defaultValue: OPERATOR_MENU_LABELS[op] })
      }
      return t(`filters.operators.${op}`, { defaultValue: OPERATOR_LABELS[op] })
    },
    [t],
  )

  const valueLabel = useCallback(
    (filter: TaskFilter, val: FilterValue): string => {
      switch (filter.fieldKey) {
        case 'status':
          return lookupMaps.status.get(val as string) ?? String(val)
        case 'priority':
          return t(`filters.priorityLevels.${val}`, { defaultValue: String(val) })
        case 'assignee':
          return val === ''
            ? t('filters.unassigned')
            : (lookupMaps.member.get(val as string) ?? String(val))
        case 'createdBy':
          return lookupMaps.member.get(val as string) ?? String(val)
        case 'due':
          if (isDueDateOperator(filter.operator)) return fmt.date(new Date(Number(val)), { weekday: 'short', month: 'short', day: 'numeric' })
          return t(`filters.due.${val as DuePreset}`, {
            defaultValue: (DUE_PRESET_LABELS as Partial<Record<string, string>>)[String(val)] ?? String(val),
          })
        case 'labels':
          return lookupMaps.label.get(val as string) ?? String(val)
        case 'project':
          return lookupMaps.project.get(val as string) ?? String(val)
        case 'cycle':
          return lookupMaps.cycle.get(val as string) ?? String(val)
        default:
          return String(val)
      }
    },
    [lookupMaps, fmt, t],
  )

  /** The value part of a chip: "In progress or Planned", "3 labels", "any". */
  const valueSummary = useCallback(
    (filter: TaskFilter): string => {
      const { fieldKey, values } = filter
      const def = getFieldDef(fieldKey)
      if (values.length === 0 || !def) return t('filters.any')
      if (def.fieldType === 'text') return `"${values[0] ?? ''}"`
      if (values.length <= 2) {
        return values.map((v) => valueLabel(filter, v)).join(` ${t('filters.or')} `)
      }
      return t('filters.manyValues', { count: values.length, label: fieldLabel(fieldKey, 'plural') })
    },
    [valueLabel, fieldLabel, t],
  )

  /** The whole chip as a sentence: "Due before Fri, Oct 2". */
  const describe = useCallback(
    (filter: TaskFilter): string =>
      `${fieldLabel(filter.fieldKey)} ${operatorLabel(filter.operator)} ${valueSummary(filter)}`,
    [fieldLabel, operatorLabel, valueSummary],
  )

  return { fieldLabel, operatorLabel, valueSummary, describe }
}
